/**
 * ToolInvocationChip — surfaces a single tool call attached to a chat
 * message. Pure presentational. Uses `lucide-react` icons for status (so we
 * inherit a consistent stroke style with the rest of the chrome) and a
 * Wayne-specific monospace tool name + duration.
 *
 * Wayne-specific shape: `(toolName, status, durationMs?, error?)`. This
 * molecule does not wrap a third-party badge — a status icon + mono label
 * conveys "this tool ran X ms" more clearly than a colored pill.
 *
 * Post-ADR-001: no Cloudscape imports. Status icon comes from `lucide-react`.
 */
import { Check, Loader, X } from 'lucide-react';
import type { ReactElement } from 'react';

export type ToolStatus = 'pending' | 'success' | 'error';

export interface ToolInvocation {
  readonly id: string;
  readonly toolName: string;
  readonly status: ToolStatus;
  readonly durationMs?: number;
  readonly error?: string;
}

function StatusIcon({ status }: { readonly status: ToolStatus }): ReactElement {
  if (status === 'pending') {
    return (
      <Loader
        size={12}
        className="animate-spin text-wayne-ink-3"
        aria-hidden="true"
      />
    );
  }
  if (status === 'success') {
    return (
      <Check
        size={12}
        className="text-wayne-green"
        aria-hidden="true"
      />
    );
  }
  return (
    <X
      size={12}
      className="text-wayne-red"
      aria-hidden="true"
    />
  );
}

export default function ToolInvocationChip({
  toolName,
  status,
  durationMs,
  error,
}: Omit<ToolInvocation, 'id'>): ReactElement {
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border border-wayne-hairline bg-wayne-surface-2 text-wayne-ink font-mono text-[11px] max-w-full min-w-0"
      data-tool-status={status}
      title={error ?? toolName}
      aria-label={`Tool ${toolName} ${status}${
        durationMs !== undefined ? ` in ${durationMs} milliseconds` : ''
      }${error ? `; ${error}` : ''}`}
    >
      <StatusIcon status={status} />
      <span className="overflow-hidden text-ellipsis whitespace-nowrap min-w-0 max-w-[40ch] text-wayne-ink">
        {toolName}
      </span>
      {durationMs !== undefined ? (
        <span className="text-wayne-ink-3">{durationMs}ms</span>
      ) : null}
    </span>
  );
}
